import React, { useState, useCallback, useEffect, useRef } from "react";
import DataGrid, {
  Column,
  Export,
  FilterRow,
  HeaderFilter,
  FilterPanel,
  Scrolling,
  LoadPanel,
  SearchPanel,
  ColumnChooser,
  ColumnChooserSearch,
  ColumnChooserSelection,
  Position,
  Paging,
  Pager,
  StateStoring,
} from "devextreme-react/data-grid";
import Button1 from "@material-ui/core/Button";
import Select from "react-select";
import { MultiSelect } from "react-multi-select-component";
import TextField from "@material-ui/core/TextField";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid } from "devextreme/excel_exporter";
import "whatwg-fetch";
import { useStyles } from "../../Styles.js";
import Grid from "@material-ui/core/Grid";
import Container from "@material-ui/core/Container";
import useDeviceSize from "../../hooks/useDeviceSize";
import { api_path } from "../../shared.js";
import DateBox from "devextreme-react/date-box";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const JOB_TYPE_LIST = [
  { value: "FCL",    label: "FCL" },
  { value: "LCL",    label: "LCL" },
  { value: "AIR",    label: "AIR" },
  { value: "IMPORT", label: "IMPORT" },
];

const JOB_STATUS_LIST = [
  { value: "BOOKING",     label: "Booking" },
  { value: "IN PROCESS",  label: "In Process" },
  { value: "SHIPPED",     label: "Shipped" },
  { value: "COMPLETED",   label: "Completed" },
  { value: "CANCELLED",   label: "Cancelled" },
];

const PAGE_SIZES      = [25, 50, 100];
const DATE_LABEL      = { "aria-label": "Date" };
const SEARCH_OPTIONS  = { placeholder: "Search column" };
const SELECT_STYLES   = { menu: (p) => ({ ...p, zIndex: 9999 }) };
const MULTISELECT_STRINGS = { selectSomeItems: "Select..." };

// ── ✅ SHIPPER COLUMN CONFIG ─────────────────────────────────────────────────
// กำหนด columns ที่ต้องการ SHOW เพิ่ม / HIDE สำหรับลูกค้าแต่ละราย
// - show: ["field"] → แสดง column นั้นแม้ default hide: true
// - hide: ["field"] → ซ่อน column นั้นแม้ default visible
// key = shipper_name ที่ตรงกับ API (case sensitive)
const SHIPPER_COLUMN_CONFIG = {
  "EXAMPLE AIR CUSTOMER": {
    show: ["invoice_total_thb", "cost_total", "profit_total",
           "ocean_freight_usd", "baf_usd", "thc_per_container_thb"],
    hide: [],
  },

  "TOTALENERGIES CORBION LTD.": {
    show: [
       "title",
      // Milestone Planned
      "ms_2003_etd_pol", "ms_4003_etd_ts1", "ms_5001_eta_del",
      // Bill / Charges
      "exchange_rate_usd_thb", "invoice_total_thb",
      "wht_pct", "wht_amount", "vat_pct", "vat_amount",
      "transportation_origin_thb", "thc_per_container_thb",
      "seal_fee_thb", "bl_fee_thb", "surrender_thb",
      "ams_fee_usd", "ams_fee_thb",
      "ocean_freight_usd", "baf_usd", "peak_season_surcharge_usd",
      "ocean_freight_total_thb", "transportation_dest_usd", "invoice_date",
      "emergency_bunker_surcharge_usd",
      "all_destination_costs_usd", "all_on_carriage_cost_usd",
      "cancellation_charge_thb", "thc_per_container_usd",
      "eir_thb", "doc_fee_thb", "do_exch_fee_thb",
      "agent_handling_usd", "agent_handling_thb",
      "all_origin_charge_usd", "all_origin_charge_thb",
      // KPI
      "kpi_arrive", "kpi_depart",
      // Extras
      "remark_1",
      "etd_pol_1st_booking", "etd_pol_latest_booking", "eta_delivery_1st_booking",
      "uq2_kgs",
    ],
    hide: [
      "sold_by", "agent_destination_name", "agent_origin_name",
      "cargo_ready_date", "forwarder", "payer_name",
      "cbm", "owner_of_container",
      "cargo_type", "vessel_type",
      "origin_custom_clearance", "origin_transportation",
      "destination_custom_clearance", "destination_transportation",
      "co_loader",
      "si_cutoffdate", "bl_amendment_cutoffdate", "fcl_cutoff", "vgm_cutoff",
      "return_date", "first_return_date_without_charge",
      "Country of Place of Receipt", "Country of Port of Discharge",
      "place_of_receipt", "port_of_discharge",
      "tranship_port_1", "tranship_port_2", "tranship_port_3", "tranship_port_4",
      "country_of_destination",
      "flight_date_1st", "flight_date_2nd", "flight_no_1st", "flight_no_2nd",
      "etd_port_of_loading", "eta_port_of_discharge", "eta_place_of_delivery", "eta_final_destination",
      "bl_no", "ams_aci_by", "bl_type", "house_bl_type", "seal_no",
      "commodity", "hscode", "eta", "etd",
      "gross_weight_air", "net_weight_air",
      "ms_4002_ata_ts1", "ms_4006_ata_ts2", "ms_4008_atd_ts2",
      "ms_4010_ata_ts3", "ms_4012_atd_ts3", "ms_4014_ata_ts4", "ms_4016_atd_ts4",
      "ms_3002_ata_pod", "ms_3004_atd_pod",
      "ms_6001_container_ready_pickup", "ms_6002_container_gateout_dest", "ms_6003_empty_container_returned",
      "ata_final_dest",
      "estimated_eta", "estimated_transit_days", "year_no",
      "delivery_completed", "delivery_completed_at",
    ],
  },

  "PURAC (THAILAND) LTD.": {
    show: [
       "title",
      // Service
      "service_contract_owner",
      // Milestone Planned
      "ms_2003_etd_pol",
      "ms_4001_eta_ts1", "ms_4003_etd_ts1", "ms_4005_eta_ts2",
      "ms_3001_eta_pod", "ms_3003_etd_pod",
      "ms_5001_eta_del", "eta_final_dest",
      "ms_1000_container_pickup_por", "ms_1001_container_return_por",
      "ms_2001_container_gatein_pol",
      // Dates
      "customer_booking_request_date", "sino_booking_received_date", "booking_release_date",
      // Bill / Charges
      "exchange_rate_usd_thb", "invoice_total_thb",
      "wht_pct", "wht_amount", "vat_pct", "vat_amount",
      "seal_fee_thb", "bl_fee_thb", "surrender_thb",
      "ams_fee_usd", "booking_fee_usd",
      "lcl_selling_rate_usd",
      "ocean_freight_usd", "baf_usd", "peak_season_surcharge_usd",
      "ocean_freight_total_thb", "invoice_date",
      "emergency_bunker_surcharge_usd", "haz_cargo_charge_usd",
      "ets_surcharge_usd", "all_destination_costs_usd", "all_on_carriage_cost_usd",
      "dap_inland_transportation_usd", "dap_imo_surcharge_usd",
      "storage_charge_thb", "cancellation_charge_thb", "thc_per_container_usd",
      "co2_emissions",
      // Remarks + booking confirmation
      "remark_1", "remark_2", "remark_3",
      "etd_pol_1st_booking", "etd_pol_latest_booking", "eta_delivery_1st_booking",
    ],
    hide: [
      "sold_by", "agent_destination_name", "agent_origin_name",
      "cargo_ready_date", "forwarder", "payer_name",
      "vessel_type", "origin_custom_clearance", "origin_transportation",
      "destination_custom_clearance", "destination_transportation",
      "si_cutoffdate", "bl_amendment_cutoffdate", "fcl_cutoff", "vgm_cutoff",
      "return_date", "first_return_date_without_charge",
      "Country of Place of Receipt", "Country of Port of Loading",
      "tranship_port_3", "tranship_port_4",
      "flight_date_1st", "flight_date_2nd", "flight_no_1st", "flight_no_2nd",
      "etd_port_of_loading", "eta_port_of_discharge", "eta_place_of_delivery",
      "eta_final_destination",
      "ams_aci_by", "bl_type", "house_bl_type", "commodity", "hscode", "eta", "etd",
      "gross_weight_air", "net_weight_air",
      "ms_4010_ata_ts3", "ms_4012_atd_ts3", "ms_4014_ata_ts4", "ms_4016_atd_ts4",
      "estimated_eta", "number_of_teu", "year_no", "month_atd", "month_eta",
      "etd1", "eta1", "eta_sta_diff",
      "shipment_status", "delivery_completed", "delivery_completed_at", "bl_status",
    ],
  },

  "DARAMIC (THAILAND) LIMITED": {
    show: [
      // Milestone Planned
       "title","ms_1000_container_pickup_por", "ms_1001_container_return_por",
      "ms_2003_etd_pol", "ms_3001_eta_pod", "ms_3003_etd_pod",
      "ms_5001_eta_del", "eta_final_dest",
      // Special fields
      "hawb", "mawb",
      "customer_booking_request_date", "sino_booking_received_date",
      // Bill / Charges
      "exchange_rate_usd_thb", "invoice_total_thb",
      "wht_pct", "wht_amount", "vat_pct", "vat_amount",
      "transportation_origin_thb", "lift_on_thb", "lift_off_thb",
      "gate_charge_thb", "insurance_thb", "thc_per_container_thb",
      "seal_fee_thb", "bl_fee_thb", "surrender_thb", "ams_fee_usd",
      "air_freight_rate_thb_per_kg", "lcl_selling_rate_usd", "lcl_inland_selling_rate_usd",
      "baf_usd", "ocean_freight_thb", "ocean_freight_total_thb",
      "transportation_dest_usd", "invoice_date", "ocean_freight_cost", "air_freight_cost_per_kg",
    ],
    hide: [
      "sold_by", "owner_of_container", "weight", "cbm",
      "co_loader", "return_date", "first_return_date_without_charge",
      "tranship_port_2", "tranship_port_3", "tranship_port_4",
      "flight_date_2nd", "flight_no_2nd",
      "ams_aci_by", "bl_type", "house_bl_type", "commodity", "hscode", "eta",
      "payer_name", "net_weight_air",
      "ms_4002_ata_ts1", "ms_4004_atd_ts1", "ms_4006_ata_ts2", "ms_4008_atd_ts2",
      "ms_4010_ata_ts3", "ms_4012_atd_ts3", "ms_4014_ata_ts4", "ms_4016_atd_ts4",
      "ms_6002_container_gateout_dest",
      "number_of_container_45",
      "estimated_eta", "estimated_transit_days", "actual_transit_days",
      "week_no", "year_no", "month_atd", "month_eta",
      "etd", "etd1", "eta1", "eta_sta_diff",
      "shipment_status", "delivery_completed", "delivery_completed_at", "bl_status",
    ],
  },
};
// ────────────────────────────────────────────────────────────────────────────

const fmtDate = (val) => {
  if (val === null || val === undefined) return "";
  const str = String(val).trim();
  if (!str) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const [y, m, d] = str.split("-");
    return `${d}/${m}/${y}`;
  }
  return str;
};
const dateCustomize = ({ value }) => fmtDate(value);
// ── Format helpers (module level) ───────────────────────────────
const FORMAT_MAP = {
  currency: { type: "fixedPoint", precision: 2 },
  number:   { type: "fixedPoint", precision: 2 },
  integer:  { type: "fixedPoint", precision: 0 },
};

// ── customizeText สำหรับ field ที่เป็น string แต่ต้องการ format ──
const makeCurrencyText = ({ value }) => {
  if (value === null || value === undefined || value === "") return "";
  const n = parseFloat(value);
  if (isNaN(n)) return value;
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const makeIntegerText = ({ value }) => {
  if (value === null || value === undefined || value === "") return "";
  const n = parseFloat(value);
  if (isNaN(n)) return value;
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};


const COLUMNS = [
  // General
  { field: "title",                         cap: "Job No.",                      w: 130, fixed: true },
  { field: "shipper_name",                   cap: "Shipper",                      w: 200 },
  { field: "shipper_addr",                   cap: "Shipper Address",              w: 220, hide: true },
  { field: "consignee_name",                 cap: "Consignee",                    w: 200 },
  { field: "consignee_addr",                 cap: "Consignee Address",            w: 220, hide: true },
  { field: "agent_destination_name",         cap: "Agent Destination",            w: 180 },
  { field: "agent_origin_name",              cap: "Agent Origin",                 w: 180 },
  { field: "sold_by",                        cap: "Sold By",                      w: 130 },
  { field: "cargo_ready_date",               cap: "Cargo Ready Date",             w: 130, date: true },
  { field: "po",                             cap: "PO",                           w: 130 },
  { field: "invoice_no",                     cap: "INV.",                  w: 140 },
  { field: "invoice_nos",                     cap: "Invoice No.",                  w: 140 },
  { field: "ref_order_no",                   cap: "Ref Order No.",                w: 140 },
  // Container
  { field: "number_of_container_20",         cap: "20'",                          w: 70 ,  fmt: "number"},
  { field: "number_of_container_40",         cap: "40'",                          w: 70 ,  fmt: "number"},
  { field: "number_of_container_40hc",       cap: "40'HC",                        w: 70,  fmt: "number" },
  { field: "number_of_container_45",         cap: "45'",                          w: 70 ,  fmt: "number"},
  { field: "container_qty_type",             cap: "container_qty_type",           w: 130},
  { field: "type_of_container",              cap: "Container Type",               w: 130},
  { field: "owner_of_container",             cap: "Container Owner",              w: 140},
  { field: "weight",                         cap: "Weight",                       w: 90,  fmt: "number" },
  { field: "cbm",                            cap: "CBM",                          w: 90 ,  fmt: "number"},
  // Shipment Info
  { field: "loading_type",                   cap: "Loading Type",                 w: 120 },
  { field: "invoice_inco_term",              cap: "Incoterm",                     w: 100 },
  { field: "cargo_type",                     cap: "Cargo Type",                   w: 120 },
  { field: "vessel_type",                    cap: "Vessel Type",                  w: 120 },
  { field: "origin_custom_clearance",        cap: "Origin Custom Clearance",      w: 180 },
  { field: "origin_transportation",          cap: "Origin Transportation",        w: 180 },
  { field: "destination_custom_clearance",   cap: "Destination Custom Clearance", w: 200 },
  { field: "destination_transportation",     cap: "Destination Transportation",   w: 200 },
  { field: "co_loader",                      cap: "Co-Loader",                    w: 140 },
  { field: "booking_no",                     cap: "Booking No.",                  w: 140 },
  // Cutoff Dates
  { field: "si_cutoffdate",                  cap: "SI Cutoff",                    w: 110, date: true },
  { field: "bl_amendment_cutoffdate",        cap: "B/L Amendment Cutoff",         w: 160, date: true },
  { field: "fcl_cutoff",                     cap: "FCL Cutoff",                   w: 110, date: true },
  { field: "vgm_cutoff",                     cap: "VGM Cutoff",                   w: 110, date: true },
  { field: "return_date",                    cap: "Return Date",                  w: 110, date: true },
  { field: "first_return_date_without_charge", cap: "1st Return (Free)",          w: 160, date: true },
  // Route
  { field: "Country of Place of Receipt",    cap: "Country of Place of Receipt",  w: 200 },
  { field: "Country of Port of Loading",     cap: "Country of Port of Loading",   w: 200 },
  { field: "Country of Port of Discharge",   cap: "Country of Port of Discharge", w: 210 },
  { field: "Country of Port of Delivery",    cap: "Country of Port of Delivery",  w: 200 },
  { field: "place_of_receipt",               cap: "Place of Receipt",             w: 150 },
  { field: "port_of_loading",                cap: "Port of Loading",              w: 150 },
  { field: "port_of_discharge",              cap: "Port of Discharge",            w: 150 },
  { field: "port_of_delivery",               cap: "Port of Delivery",             w: 150 },
  { field: "tranship_port_1",                cap: "T/S Port 1",                   w: 130 },
  { field: "tranship_port_2",                cap: "T/S Port 2",                   w: 130 },
  { field: "tranship_port_3",                cap: "T/S Port 3",                   w: 130 },
  { field: "tranship_port_4",                cap: "T/S Port 4",                   w: 130 },
  { field: "country_of_destination",         cap: "Country of Destination",       w: 180 },
  // Flight / Vessel
  { field: "flight_date_1st",                cap: "Flight Date 1st",              w: 130, date: true },
  { field: "flight_date_2nd",                cap: "Flight Date 2nd",              w: 130, date: true },
  { field: "etd_port_of_loading",            cap: "ETD Port of Loading",          w: 150, date: true },
  { field: "eta_port_of_discharge",          cap: "ETA Port of Discharge",        w: 160, date: true },
  { field: "eta_place_of_delivery",          cap: "ETA Place of Delivery",        w: 160, date: true },
  { field: "eta_final_destination",          cap: "ETA Final Destination",        w: 160, date: true },
  { field: "flight_no_1st",                  cap: "Flight No. 1st",               w: 130 },
  { field: "flight_no_2nd",                  cap: "Flight No. 2nd",               w: 130 },
  { field: "bl_no",                          cap: "B/L No.",                      w: 150 },
  { field: "ams_aci_by",                     cap: "AMS/ACI By",                   w: 130 },
  { field: "vessel_name_1st",                cap: "Vessel",                       w: 150 },
  { field: "vessel_voyage_1st",              cap: "Voyage",                       w: 100 },
  { field: "container_no",                   cap: "Container No.",                w: 150 },
  { field: "container_size",                 cap: "Container Size",               w: 120 },
  { field: "seal_no",                        cap: "Seal No.",                     w: 120 },
  { field: "quantity",                       cap: "Quantity",                     w: 150 },
  { field: "unit_of_quantity",               cap: "Unit of Quantity",             w: 150 },
  { field: "bl_type",                        cap: "B/L Type",                     w: 100 },
  { field: "house_bl_type",                  cap: "House B/L Type",               w: 130 },
  { field: "commodity",                      cap: "Commodity",                    w: 160 },
  { field: "hscode",                         cap: "HS Code",                      w: 120 },
  { field: "eta",                            cap: "ETA",                          w: 110, date: true },
  { field: "etd",                            cap: "ETD",                          w: 110, date: true },
  // Milestone Planned (hidden)
  { field: "ms_2003_etd_pol",                cap: "2003 : ETD Port of Loading",                       w: 120, date: true, hide: true },
  { field: "ms_4001_eta_ts1",                cap: "4001 : ETA Transshipment Port 1",                  w: 120, date: true, hide: true },
  { field: "ms_4003_etd_ts1",                cap: "4003 : ETD Transshipment Port 1",                  w: 120, date: true, hide: true },
  { field: "ms_4005_eta_ts2",                cap: "4005 : ETA Transshipment Port 2",                  w: 120, date: true, hide: true },
  { field: "ms_4007_etd_ts2",                cap: "4007 : ETD Transshipment Port 2",                  w: 120, date: true, hide: true },
  { field: "ms_4009_eta_ts3",                cap: "4009 : ETA Transshipment Port 3",                  w: 120, date: true, hide: true },
  { field: "ms_4011_etd_ts3",                cap: "4011 : ETD Transshipment Port 3",                  w: 120, date: true, hide: true },
  { field: "ms_4013_eta_ts4",                cap: "4013 : ETA Transshipment Port 4",                  w: 120, date: true, hide: true },
  { field: "ms_4015_etd_ts4",                cap: "4015 : ETD Transshipment Port 4",                  w: 120, date: true, hide: true },
  { field: "ms_3001_eta_pod",                cap: "3001 : ETA Port of Discharge",                     w: 120, date: true, hide: true },
  { field: "ms_3003_etd_pod",                cap: "3003 : ETD Port of Discharge",                     w: 120, date: true, hide: true },
  { field: "ms_5001_eta_del",                cap: "5001 : ETA Place of Delivery",                     w: 120, date: true, hide: true },
  { field: "eta_final_dest",                 cap: "ETA Final Destination",                            w: 140, date: true, hide: true },
  { field: "ms_1000_container_pickup_por",   cap: "1000 : Container Pick Up Date at Place of Receipt",w: 180, date: true, hide: true },
  { field: "ms_1001_container_return_por",   cap: "1001 : Container Return Date at Place of Receipt", w: 180, date: true, hide: true },
  { field: "ms_2001_container_gatein_pol",   cap: "2001 : Container Gate In Date at Port of Loading", w: 190, date: true, hide: true },
  // Milestone Actual
  { field: "ms_2004_atd_pol",                cap: "2004 : ATD Port of Loading",          w: 120, date: true },
  { field: "ms_4002_ata_ts1",                cap: "4002 : ATA Transshipment Port 1",     w: 120, date: true },
  { field: "ms_4004_atd_ts1",                cap: "4004 : ATD Transshipment Port 1",     w: 120, date: true },
  { field: "ms_4006_ata_ts2",                cap: "4006 : ATA Transshipment Port 2",     w: 120, date: true },
  { field: "ms_4008_atd_ts2",                cap: "4008 : ATD Transshipment Port 2",     w: 120, date: true },
  { field: "ms_4010_ata_ts3",                cap: "4010 : ATA Transshipment Port 3",     w: 120, date: true },
  { field: "ms_4012_atd_ts3",                cap: "4012 : ATD Transshipment Port 3",     w: 120, date: true },
  { field: "ms_4014_ata_ts4",                cap: "4014 : ATA Transshipment Port 4",     w: 120, date: true },
  { field: "ms_4016_atd_ts4",                cap: "4016 : ATD Transshipment Port 4",     w: 120, date: true },
  { field: "ms_3002_ata_pod",                cap: "3002 : ATA Port of Discharge",        w: 120, date: true },
  { field: "ms_3004_atd_pod",                cap: "3004 : ATD Port of Discharge",        w: 120, date: true },
  { field: "ms_5002_ata_del",                cap: "5002 : ATA Place of Delivery",        w: 120, date: true },
  { field: "ms_6001_container_ready_pickup", cap: "6001 : Container Ready for Pick Up",  w: 190, date: true },
  { field: "ms_6002_container_gateout_dest", cap: "6002 : Container Gate Out at Destination", w: 200, date: true },
  { field: "ms_6003_empty_container_returned", cap: "6003 : Empty Container Returned",   w: 210, date: true },
  { field: "ata_final_dest",                 cap: "ata_final_dest",                      w: 210, date: true },
  // Bill / Charges
  { field: "invoice_total_thb",          cap: "Invoice Total Amount(THB)",                    w: 160, hide: true, txtFmt: "currency"  },
  { field: "cost_total",                 cap: "Cost Total",                                   w: 130, hide: true, txtFmt: "currency"  },
  { field: "profit_total",               cap: "Profit Total",                                 w: 130, hide: true, txtFmt: "currency"  },
  { field: "exchange_rate_usd_thb",      cap: "Exchange Rate (USD/THB)",                      w: 170, hide: true , txtFmt: "currency" },
  { field: "vat_pct",                    cap: "VAT %",                                        w: 80,  hide: true, txtFmt: "currency"  },
  { field: "wht_pct",                    cap: "WHT %",                                        w: 80,  hide: true , txtFmt: "currency" },
  { field: "vat_amount",                 cap: "VAT Amount",                                   w: 120, hide: true, txtFmt: "currency"  },
  { field: "wht_amount",                 cap: "WHT Amount",                                   w: 120, hide: true , txtFmt: "currency" },
  { field: "transportation_origin_thb",  cap: "Transportation Origin (THB)",                  w: 200, hide: true , txtFmt: "currency" },
  { field: "lift_on_thb",                cap: "Lift On (THB)",                                w: 120, hide: true , txtFmt: "currency" },
  { field: "lift_off_thb",               cap: "Lift Off (THB)",                               w: 120, hide: true , txtFmt: "currency" },
  { field: "gate_charge_thb",            cap: "Gate Charge (THB)",                            w: 140, hide: true , txtFmt: "currency" },
  { field: "insurance_thb",              cap: "Insurance (THB)",                              w: 130, hide: true , txtFmt: "currency" },
  { field: "thc_per_container_thb",      cap: "Terminal Handling Charges per Container (THB)", w: 170, hide: true , txtFmt: "currency" },
  { field: "thc_total_thb",              cap: "Terminal Handling Charges Total (THB)",         w: 140, hide: true , txtFmt: "currency" },
  { field: "seal_fee_thb",               cap: "Seal Fee (THB)",                               w: 120, hide: true , txtFmt: "currency" },
  { field: "bl_fee_thb",                 cap: "B/L Fee (THB)",                                w: 120, hide: true , txtFmt: "currency" },
  { field: "surrender_thb",              cap: "Surrender (THB)",                              w: 130, hide: true, txtFmt: "currency"  },
  { field: "ams_fee_usd",                cap: "AMS Fee (USD)",                                w: 120, hide: true , txtFmt: "currency" },
  { field: "ams_fee_thb",                cap: "AMS Fee (THB)",                                w: 120, hide: true , txtFmt: "currency" },
  { field: "booking_fee_usd",            cap: "Booking Fee (USD)",                            w: 140, hide: true, txtFmt: "currency"  },
  { field: "ocean_freight_usd",          cap: "Ocean Freight (USD)",                          w: 160, hide: true , txtFmt: "currency" },
  { field: "ocean_freight_thb",          cap: "Ocean Freight (THB)",                          w: 160, hide: true , txtFmt: "currency" },
  { field: "ocean_freight_total_thb",    cap: "Ocean Freight Total (THB)",                    w: 190, hide: true , txtFmt: "currency" },
  { field: "baf_usd",                    cap: "BAF (USD)",                                    w: 100, hide: true, txtFmt: "currency"  },
  { field: "peak_season_surcharge_usd",  cap: "Peak Season Surcharge (USD)",                  w: 200, hide: true, txtFmt: "currency"  },
  { field: "transportation_dest_usd",    cap: "Transportation Destination (USD)",              w: 220, hide: true , txtFmt: "currency" },
  { field: "customs_clearance_thb",      cap: "Customs Clearance (THB)",                      w: 180, hide: true , txtFmt: "currency" },
  { field: "demurrage_thb",              cap: "Demurrage (THB)",                              w: 130, hide: true , txtFmt: "currency" },
  { field: "detention_thb",              cap: "Detention (THB)",                              w: 130, hide: true , txtFmt: "currency" },
  { field: "eir_thb",                    cap: "EIR (THB)",                                    w: 110, hide: true , txtFmt: "currency" },
  { field: "doc_fee_thb",                cap: "DOC FEE (THB)",                                w: 130, hide: true , txtFmt: "currency" },
  { field: "do_exch_fee_thb",            cap: "DO EXCH FEE (THB)",                            w: 140, hide: true , txtFmt: "currency" },
  { field: "agent_handling_usd",         cap: "Agent Handling (USD)",                         w: 160, hide: true , txtFmt: "currency" },
  { field: "agent_handling_thb",         cap: "Agent Handling (THB)",                         w: 160, hide: true, txtFmt: "currency"  },
  { field: "all_origin_charge_usd",      cap: "All Origin Charge (USD)",                      w: 170, hide: true , txtFmt: "currency" },
  { field: "all_origin_charge_thb",      cap: "All Origin Charge (THB)",                      w: 170, hide: true , txtFmt: "currency" },
  { field: "remark_1",                   cap: "remark_1",                                     w: 130, hide: true },
  { field: "remark_2",                   cap: "remark_2",                                     w: 130, hide: true },
  { field: "remark_3",                   cap: "remark_3",                                     w: 130, hide: true },
  // Calculated
  { field: "estimated_eta",              cap: "Estimated ETA",                                w: 130, date: true },
  { field: "estimated_transit_days",     cap: "Estimated Transit (Days)",                     w: 170 },
  { field: "actual_transit_days",        cap: "Actual Transit (Days)",                        w: 170 },
  { field: "number_of_teu",              cap: "TEU",                                          w: 80  },
  { field: "week_no",                    cap: "Week",                                         w: 70  },
  { field: "year_no",                    cap: "Year",                                         w: 70  },
  { field: "month_atd",                  cap: "Month ATD",                                    w: 110 },
  { field: "month_eta",                  cap: "Month ETA (Place of Delivery)",                w: 190 },
  { field: "etd1",                       cap: "ETD & ATD Difference (Days)",                  w: 180 },
  { field: "eta1",                       cap: "ETA & ATA Difference (Days)",                  w: 180 },
  { field: "eta_sta_diff",               cap: "ETA-STA Difference (Days)",                    w: 180 },
  { field: "shipment_status",            cap: "Shipment Status",                              w: 140 },
  { field: "delivery_completed",         cap: "Delivery Completed",                           w: 150 },
  { field: "delivery_completed_at",      cap: "Delivery Completed At",                        w: 170, date: true },
  // New Charge Fields
  { field: "co2_emissions",              cap: "CO2 emissions",                                w: 130, hide: true },
  { field: "emergency_bunker_surcharge_usd", cap: "Emergency Bunker Surcharge (USD)",         w: 200, hide: true , fmt: "currency" },
  { field: "haz_cargo_charge_usd",       cap: "HAZ Cargo Charge (USD)",                       w: 170, hide: true , fmt: "currency" },
  { field: "ets_surcharge_usd",          cap: "ETS Surcharge (USD)",                          w: 150, hide: true , fmt: "currency" },
  { field: "all_destination_costs_usd",  cap: "All Destination Costs (USD)",                  w: 190, hide: true , fmt: "currency" },
  { field: "all_on_carriage_cost_usd",   cap: "All On Carriage Cost (USD)",                   w: 190, hide: true , fmt: "currency" },
  { field: "dap_inland_transportation_usd", cap: "DAP USA: Inland Transportation (USD)",      w: 220, hide: true , fmt: "currency" },
  { field: "dap_imo_surcharge_usd",      cap: "DAP USA: IMO Surcharge (USD)",                 w: 200, hide: true , fmt: "currency" },
  { field: "storage_charge_thb",         cap: "Storage Charge (THB)",                         w: 160, hide: true , fmt: "currency" },
  { field: "storage_charge_usd",         cap: "Storage Charge (USD)",                         w: 160, hide: true, fmt: "currency"  },
  { field: "cancellation_charge_thb",    cap: "Cancellation Charge (THB)",                    w: 180, hide: true , fmt: "currency" },
  { field: "thc_per_container_usd",      cap: "THC per Container (USD)",                      w: 170, hide: true , fmt: "currency" },
  { field: "bl_status",                  cap: "B/L Status",                                   w: 120 },

  { field: "gross_weight_air",           cap: "Gross weight (kgs) - Air",                     w: 160 },
  { field: "net_weight_air",             cap: "Net weight (KGS) - Air",                       w: 160 },
  { field: "forwarder",                  cap: "Forwarder",                                    w: 150 },
  { field: "payer_name",                 cap: "Payer Name",                                   w: 150 },
  { field: "ocean_carrier",              cap: "Ocean Carrier / Airline",                      w: 150 },
  { field: "service_contract_owner",     cap: "Service Contract Owner",                       w: 150, hide: true },
  { field: "contract_type",              cap: "Contract Type",                                w: 150, hide: true },
  { field: "flight_date_3rd",            cap: "Flight Date 3rd",                              w: 150, date: true, hide: true },
  { field: "flight_date_4th",            cap: "Flight Date 4th",                              w: 150, date: true, hide: true },
  { field: "flight_no_3rd",              cap: "Flight No. 3rd",                               w: 150, hide: true },
  { field: "hawb",                       cap: "HAWB",                                         w: 150, hide: true },
  { field: "mawb",                       cap: "MAWB",                                         w: 150, hide: true },
  { field: "mbl_scac",                   cap: "MBL SCAC",                                     w: 150, hide: true },
  { field: "vessel_name_2nd",            cap: "2nd Vessel Voyage",                            w: 150, hide: true },
  { field: "vessel_name_3rd",            cap: "3rd Vessel Name",                              w: 150, hide: true },
  { field: "vessel_voyage_3rd",          cap: "3rd Vessel Voyage",                            w: 150, hide: true },
  { field: "vessel_name_4th",            cap: "4th Vessel Name",                              w: 150, hide: true },
  { field: "vessel_voyage_4th",          cap: "4th Vessel Voyage",                            w: 150, hide: true },
  { field: "customer_booking_request_date", cap: "Customer's Booking Request Date",           w: 150, date: true, hide: true },
  { field: "sino_booking_received_date", cap: "SINO's Booking Received Date",                 w: 150, date: true, hide: true },
  { field: "booking_release_date",       cap: "Booking Release Date",                         w: 150, date: true, hide: true },
  { field: "origin_customs_clearance_date", cap: "Origin Customs Clearance Date",             w: 150, date: true, hide: true },
  { field: "air_freight_rate_thb_per_kg", cap: "Air Freight Rate (THB/kg)",                   w: 150, hide: true },
  { field: "lcl_selling_rate_usd",       cap: "LCL Selling Rate (USD/CBM/TON)",               w: 150, hide: true },
  { field: "lcl_inland_selling_rate_usd", cap: "LCL Inland Selling Rate (USD/unit)",          w: 150, hide: true },
  { field: "invoice_date",               cap: "Invoice Date",                                 w: 150, date: true, hide: true },
  { field: "ocean_freight_cost",         cap: "Ocean Freight Cost",                           w: 150, hide: true , fmt: "currency" },
  { field: "air_freight_cost_per_kg",    cap: "Air Freight Cost per kg",                      w: 150, hide: true , fmt: "currency" },
  { field: "expected_delayed_days",      cap: "Expected Delayed Days from 1st ETA",           w: 150, hide: true },
  { field: "kpi_arrive",                 cap: "KPI Arrive",                                   w: 150, hide: true },
  { field: "kpi_depart",                 cap: "KPI Depart",                                   w: 150, hide: true },
  { field: "isf_date",                   cap: "ISF Date",                                     w: 150, date: true, hide: true },
  { field: "isf_status",                 cap: "ISF Status",                                   w: 150, hide: true },
  { field: "arrival_notice_issued_date", cap: "Arrival Notice Issued Date",                   w: 150, date: true, hide: true },
  { field: "demurrage_freetime_dest",    cap: "Demurrage Freetime at Destination",            w: 150, hide: true },
  { field: "detention_freetime_dest",    cap: "Detention Freetime at Destination",            w: 150, hide: true },
  { field: "last_free_day_dest",         cap: "Last Free Day at Destination",                 w: 150, date: true, hide: true },
  { field: "etd_pol_1st_booking",        cap: "ETD POL - 1st Booking Confirmation",           w: 150, date: true, hide: true },
  { field: "etd_pol_latest_booking",     cap: "ETD POL - Latest Booking",                     w: 150, date: true, hide: true },
  { field: "eta_delivery_1st_booking",   cap: "ETA Place of Delivery - 1st Booking",          w: 150, date: true, hide: true },
  { field: "uq2_kgs",                    cap: "UQ2 -> KGS",                                   w: 150, hide: true },
];

const randomInt  = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const formatDate = (d) =>
  d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" })
   .split("/").reverse().join("-");

const now      = new Date();
const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
const lastDay  = new Date(now.getFullYear(), now.getMonth() + 1, 0);

export function JobTrackingDatagrid(props) {
  const [getUserID] = useState(props.CRM_ID);
  const [, height] = useDeviceSize();
  const classes    = useStyles();
  const gridRef    = useRef(null);

  const [loading,      setLoading]      = useState(false);
  const [data,         setData]         = useState([]);
  const [shipperList,  setShipperList]  = useState([]);
  const [viewMode, setViewMode] = useState("CONTAINER");
  const [getDateFrom,   setDateFrom]    = useState(formatDate(firstDay));
  const [getDateTo,     setDateTo]      = useState(formatDate(lastDay));
  const [getMinDate,    setMinDate]     = useState(null);
  const [getJobType,    setJobType]     = useState(null);
  const [getJobStatus,  setJobStatus]   = useState(null);
  const [getSalesUser,  setSalesUser]   = useState("");
  // ✅ เปลี่ยนเป็น single select (null แทน [])
  const [selectShipper, setSelectShipper] = useState(null);

  useEffect(() => {
    fetch(`${api_path}/fc/getCustomerJobTracking.php?crm_uid=`+getUserID)
      .then((r) => r.json())
      .then((json) => {
        if (json.success && Array.isArray(json.data)) {
          setShipperList(json.data.map((item) => ({ value: item.shipper_name, label: item.shipper_name })));
        }
      })
      .catch(console.error);
  }, []);

  const fetchData = useCallback(async () => {
        // ✅ ต้องเลือก Shipper ก่อนถึงจะ fetch ข้อมูล
    if (!selectShipper?.value) {
      setData([]);
      return;
    }
    setLoading(true);
    try {
      const p = new URLSearchParams({ etd_from: getDateFrom, etd_to: getDateTo });
      if (getJobType?.value)    p.set("job_type",   getJobType.value);
      if (getJobStatus?.value)  p.set("job_status", getJobStatus.value);
      if (getSalesUser.trim())  p.set("sales_user", getSalesUser.trim());
      // ✅ single value
      if (selectShipper?.value) p.set("shipper", selectShipper.value);
      p.set("view_mode", viewMode);
      const res  = await fetch(`${api_path}/fc/getJobTracking.php?${p.toString()}`, { credentials: "include" });
      const json = await res.json();
      setData(json.success ? (json.data ?? []) : []);
    } catch (err) {
      console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [getDateFrom, getDateTo, getJobType, getJobStatus, getSalesUser, selectShipper, viewMode]);

  const onValueChangedDateFrom = useCallback((e) => {
    const f = formatDate(new Date(e.value));
    setDateFrom(f); setMinDate(f);
  }, []);
  const onValueChangedDateTo = useCallback((e) => {
    setDateTo(formatDate(new Date(e.value)));
  }, []);

  const handleSearch          = useCallback(() => fetchData(), [fetchData]);
  const onRefreshClick        = useCallback(() => fetchData(), [fetchData]);
  const handleClearInputClick = useCallback(() => {
    setJobType(null); setJobStatus(null); setSalesUser("");
    setSelectShipper(null); // ✅ reset เป็น null
    setData([]);  // ← clear grid
    setDateFrom(formatDate(firstDay)); setDateTo(formatDate(lastDay)); setMinDate(null);
  }, []);

  const onExporting = useCallback((e) => {
    // ✅ ใช้ selectShipper?.label แทน .map
    const shipperPart = selectShipper?.label
      ? selectShipper.label.replace(/[^a-zA-Z0-9ก-๙]/g, "-")
      : "All";
    const fileName = `JobTracking_${shipperPart}_${formatDate(new Date())}.xlsx`;
    const workbook  = new Workbook();
    const worksheet = workbook.addWorksheet("Job Tracking");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
      });
    });
  }, [selectShipper]);

  return (
    <Container maxWidth="xl">
      <Grid container spacing={2}>
        <Grid item xs={12}>&nbsp;</Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10}>
          <label className="head">Job Tracking</label>
        </Grid>
      </Grid>

      {/* ── Filters ── */}
      <Grid container spacing={2}>
      <Grid item xs={12} sm={2}>
  <label>View Mode</label>
  <Select
    value={{ value: viewMode, label: viewMode === "JOB" ? "By Job" : "By Container" }}
    options={[
      { value: "CONTAINER", label: "By Container" },
      { value: "JOB",       label: "By Job" },
    ]}
onChange={(e) => {
  setViewMode(e.value);
  setData([]);  // ← clear grid
}}
    styles={SELECT_STYLES}
  />
</Grid>

        <Grid item xs={12} sm={2}>
          <label>Shipper / Customer</label>
          {/* ✅ เปลี่ยนจาก MultiSelect → Select (single) */}
          <Select
            placeholder="Select shipper..."
            value={selectShipper}
            options={shipperList}
            onChange={setSelectShipper}
            isClearable
            styles={SELECT_STYLES}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <label>ETD From</label>
          <DateBox inputAttr={DATE_LABEL} type="date" displayFormat="dd/MM/yyyy"
            value={getDateFrom} onValueChanged={onValueChangedDateFrom} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <label>ETD To</label>
          <DateBox inputAttr={DATE_LABEL} type="date" displayFormat="dd/MM/yyyy"
            value={getDateTo} min={getMinDate} onValueChanged={onValueChangedDateTo} />
        </Grid>
        <Grid item xs={12} sm={6}></Grid>
        {/* Job Type / Status / Sales ยังคงไว้ใน code แต่ comment ออกตาม original */}
        {/* <Grid item xs={12} sm={2}>
          <label>Job Type</label>
          <Select placeholder="Select..." value={getJobType} options={JOB_TYPE_LIST}
            onChange={setJobType} isClearable styles={SELECT_STYLES} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <label>Status</label>
          <Select placeholder="Select..." value={getJobStatus} options={JOB_STATUS_LIST}
            onChange={setJobStatus} isClearable styles={SELECT_STYLES} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <label>Sales</label>
          <TextField fullWidth variant="outlined" size="small" placeholder="Sales name..."
            value={getSalesUser} onChange={(e) => setSalesUser(e.target.value)} />
        </Grid> */}
        <Grid item xs={12} sm={12} style={{ display: "flex" }} justify="flex-end">
          <Button1 className="searchButton" variant="contained" color="primary" onClick={handleSearch}>Search</Button1>
          &nbsp;&nbsp;&nbsp;
          <Button1 className="searchButton" variant="outlined" color="primary" onClick={handleClearInputClick}>Clear Filter</Button1>
          &nbsp;&nbsp;&nbsp;
          <Button1 className="searchButton" variant="outlined" color="primary" onClick={onRefreshClick}>Refresh data</Button1>
        </Grid>
      </Grid>

      {/* ── DataGrid ── */}
      {!selectShipper && (
        <Grid container spacing={2} style={{ marginTop: 2 }}>
          <Grid item xs={12} style={{ textAlign: "left", color: "#999", fontSize: 16 }}>
    Please select a Shipper / Customer and click Search to load data.
          </Grid>
        </Grid>
      )}
      {/* ── DataGrid ── */}
      <Grid container className={classes.root}>
        <DataGrid
          ref={gridRef}
          dataSource={data}
          keyExpr={viewMode == "JOB" ? "job_no" : ["job_no", "container_id"]}
          //keyExpr="job_no"
          showBorders={true}
          columnAutoWidth={false}
          rowAlternationEnabled={true}
          allowColumnReordering={true}
          allowColumnResizing={true}
          columnResizingMode="widget"
          columnMinWidth={50}
          showScrollbar={true}
          height={height}
          hoverStateEnabled={true}
          repaintChangesOnly={true}
          onExporting={onExporting}
          wordWrapEnabled={false}
          renderAsync={true}
          cacheEnabled={true}
        >
          <Scrolling mode="virtual" rowRenderingMode="virtual" showScrollbar="always" useNative={false} scrollByThumb={true} />
          <Paging defaultPageSize={50} />
          <Pager allowedPageSizes={PAGE_SIZES} showPageSizeSelector={true} showInfo={true} />
          <LoadPanel enabled={loading} />
          <FilterRow visible={true} />
          <FilterPanel visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Full Text Search..." />
          <ColumnChooser enabled={true} mode="select">
            <Position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button" />
            <ColumnChooserSearch enabled={true} editorOptions={SEARCH_OPTIONS} />
            <ColumnChooserSelection allowSelectAll={true} selectByClick={true} recursive={true} />
          </ColumnChooser>
            <Scrolling
                      rowRenderingMode="virtual"
                      showScrollbar="always"
                      useNative="false"
                      scrollByThumb="true"
                    ></Scrolling>
          <StateStoring enabled={true} type="localStorage" storageKey="jobTrackingGridState2" />
          <Export enabled={true} allowExportSelectedData={false} />
{COLUMNS.map(({ field, cap, w, fixed, hide, date, fmt, txtFmt }) => {
  const config = selectShipper ? SHIPPER_COLUMN_CONFIG[selectShipper.value] : null;
  let visible = !hide;
  if (config) {
    if (config.show?.includes(field)) visible = true;
    if (config.hide?.includes(field)) visible = false;
  }

  // fmt = ข้อมูลเป็น numeric จริง → ใช้ DevExtreme format
  // txtFmt = ข้อมูลเป็น string → ใช้ customizeText แปลงเอง
  // date = วันที่ → ใช้ customizeText แปลง date
  const colFormat     = (fmt && !date) ? FORMAT_MAP[fmt] : undefined;
  const colAlignment  = (fmt || txtFmt) ? "right" : undefined;

  let customText = undefined;
  if (date && !fmt)          customText = dateCustomize;
  else if (txtFmt === "currency") customText = makeCurrencyText;
  else if (txtFmt === "integer")  customText = makeIntegerText;

  return (
    <Column
      key={field}
      dataField={field}
      caption={cap}
      width={w}
      fixed={fixed ?? false}
      fixedPosition={fixed ? "left" : undefined}
      visible={visible}
      customizeText={customText}
      format={colFormat}
      alignment={colAlignment ?? "left"}
    />
  );
})}
          {/* ✅ Column visibility แยกตาม Shipper */}
          {/* {COLUMNS.map(({ field, cap, w, fixed, hide, date }) => {
            const config = selectShipper ? SHIPPER_COLUMN_CONFIG[selectShipper.value] : null;
            let visible = !hide;
            if (config) {
              if (config.show?.includes(field)) visible = true;
              if (config.hide?.includes(field)) visible = false;
            }
            return (
              <Column
                key={field}
                dataField={field}
                caption={cap}
                width={w}
                fixed={fixed ?? false}
                fixedPosition={fixed ? "left" : undefined}
                visible={visible}
                customizeText={date ? dateCustomize : undefined}
              />
            );
          })} */}
        </DataGrid>
      </Grid>
    </Container>
  );
}
